import React, { useEffect } from 'react';
import { DragDrop } from '@uppy/react';

import { ApiResponseImageUpdateType } from '@types';
import GlobalCtx from '@components/globalContext';
import { Samples, Modal, Button, Spinner } from '@components';
import { LocalStorage } from '@utils';
import UppyHelpers from './uppy';

const Import = (): JSX.Element => {
  const { setImage, image, app, setApp } = React.useContext(GlobalCtx);
  const [uppy, setUppy] = React.useState<any>(undefined);

  const onUploadError = (error?: string) => {
    setImage({
      ...image,
      isUploading: false,
    });

    setApp({
      ...app,
      notice: {
        type: 'ERROR',
        message: error || 'Unable to upload your image. Please try again later.',
        isVisible: true,
      },
    });
  };

  const onUploadStart = () => {
    toggleModal(false);
    setImage({
      ...image,
      isUploading: true,
    });
  };

  const onUploadComplete = (newProps: ApiResponseImageUpdateType) => {
    toggleModal(false);
    setImage({
      ...image,
      isUploading: false,
      imageContent: {
        ...newProps,
        uploadedAt: Date.now(),
      },
    });
    LocalStorage.updateImage(newProps);
  };

  const toggleModal = (modalIsVisible: boolean, name?: string) => {
    setApp({
      ...app,
      modal: {
        ...app.modal,
        isVisible: modalIsVisible,
        name: name,
      },
    });
  };

  useEffect(() => {
    const uppyInstance = UppyHelpers.createInstance({
      app,
      image,
      onUploadStart,
      onUploadComplete,
      onUploadError,
    });
    setUppy(uppyInstance);
  }, []);

  return (
    <div className="flex place-content-center">
      <div className="bg-white shadow-md p-6 border border-sky-100 place-content-center rounded relative">
        {image.isImporting || image.isUploading ? (
          <div className="absolute w-full text-center bg-white/80 h-full z-10 -m-6 rounded">
            <div className="flex items-center place-content-center h-full">
              <div>
                <p className="text-xl py-4">{`${
                  image.isImporting ? 'Importing' : 'Uploading'
                } your image...`}</p>
                <Spinner className="place-content-center flex mt-3" />
              </div>
            </div>
          </div>
        ) : (
          ''
        )}
        <h3 className="text-center">
          Wiremesh is a generative art playground. To get started, pick an image to work with:
        </h3>
        <section className="md:flex mt-3">
          <div className="md:flex-grow">
            <Samples />
          </div>
          <div className="flex md:pl-6">
            <span className="self-center mt-4 m-auto md:m-0 transition-all">
              <Button onClick={() => toggleModal(true, 'uploadModal')} disabled={image.isImporting}>
                Upload my own image
              </Button>
            </span>
          </div>
        </section>
        <Modal>
          {uppy && (
            <DragDrop
              className="drag-target"
              uppy={uppy}
              locale={{
                strings: {
                  dropHereOr: 'Drag an image here or %{browse}.',
                  browse: 'browse for a file',
                },
              }}
            />
          )}
        </Modal>
      </div>
    </div>
  );
};

export default Import;
